import type {ChartXAxisOptions, ChartYAxisOptions, PlotOptions, Stacking} from '../types'

export const isDev = () => process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test'

export const LINE_CHART_TYPES = ['line', 'spline', 'area', 'areaspline']
export const COLUMN_BAR_CHART_TYPES = ['column', 'bar']

export const SERIES_DASH_STYLES = [
  'Solid',
  'ShortDash',
  'Dot',
  'DashDot',
  'LongDash',
  'ShortDashDotDot',
  'ShortDot',
  'LongDashDot',
  'Dash',
  'ShortDashDot',
  'LongDashDotDot',
]

const SYMBOLS = ['circle', 'square', 'diamond', 'triangle', 'triangle-down']

// abbreviated month followed by the day (no leading zero before days 1-9)
const DEFAULT_DATE_TIME_FORMAT = '{value:%b %e}'

const getDefaultFormat = (type: string | undefined) => (type === 'datetime' ? DEFAULT_DATE_TIME_FORMAT : undefined)

export const getXAxisOptionWithDefaults = ({
  xAxis,
  gridLineDashStyle,
  gridLineWidth,
}: {
  xAxis?: ChartXAxisOptions
  gridLineDashStyle?: Highcharts.DashStyleValue
  gridLineWidth?: number
}) => {
  if (xAxis === undefined) {
    return undefined
  }

  const title = xAxis.title

  return {
    // Allow user override
    gridLineWidth,
    ...xAxis,
    labels: {
      format: getDefaultFormat(xAxis.type),
      ...xAxis.labels,
    },
    title: {
      text: title,
    },
    gridLineDashStyle,
  }
}

export const getYAxisOptionsWithDefaults = ({
  yAxis,
  gridLineDashStyle,
  gridLineWidth,
}: {
  yAxis?: ChartYAxisOptions | ChartYAxisOptions[]
  gridLineDashStyle?: Highcharts.DashStyleValue
  gridLineWidth?: number
}) => {
  if (yAxis === undefined) {
    return undefined
  }

  // convert yAxis to an array if it is not already an array
  const array = Array.isArray(yAxis) ? yAxis : [yAxis]

  const isSingleYAxis = array.length === 1

  // map over yAxis and add default format to labels
  return array.map(yAxisOption => {
    const title = yAxisOption.title

    return {
      // Allow user override
      gridLineWidth: isSingleYAxis ? gridLineWidth : 0,
      ...yAxisOption,
      labels: {
        format: getDefaultFormat(yAxisOption.type),
        ...yAxisOption.labels,
      },
      title: {
        text: title,
      },
      gridLineDashStyle,
    }
  })
}

export const getDataWithDefaults = ({
  series,
  type,
  hasDashStyle = true,
}: {
  series: Highcharts.SeriesOptionsType[]
  type?: string
  hasDashStyle?: boolean
}) => {
  return (series ?? []).map((s, index) => {
    const dashStyle = SERIES_DASH_STYLES[index % SERIES_DASH_STYLES.length]
    // only set default dashStyle if it is not already set
    return {
      type,
      dashStyle: hasDashStyle ? dashStyle : 'Solid',
      marker: {
        symbol: SYMBOLS[index % SYMBOLS.length],
      },
      ...s,
    }
  })
}

// Default values for plotOptions
// - marker: false
// - dataLabel: false
// - lineWidth: 2 - only applied to line, area, areaspline and spline charts
// - borderWidth: 1.5 - only applied to bar column and pie charts
export const getTransformedPlotOptions = ({
  plotOptions,
  marker,
  labels,
  stacking,
  noBorderRadius = false,
}: {
  plotOptions?: PlotOptions
  marker?: boolean
  labels?: boolean
  stacking?: Stacking
  noBorderRadius?: boolean
}) => {
  const defaultPlotOptions = {
    marker: {enabled: marker ?? false},
    dataLabels: {enabled: labels ?? false},
    lineWidth: 2,
    borderWidth: 1.5,
    stacking: stacking ?? undefined,
    borderRadius: noBorderRadius ? 0 : undefined,
  }

  return {
    series: {...plotOptions, ...defaultPlotOptions},
  } as Highcharts.PlotOptions
}

export const getSymbolStyles = ({
  color,
  borderColor,
  borderShape,
  symbol,
}: {
  color?: string | Highcharts.GradientColorObject | Highcharts.PatternObject
  borderColor?: string
  borderShape?: string
  symbol: (typeof SYMBOLS)[number]
}) => {
  let symbolStyle: string
  // Do not add border for triangle symbols
  const border =
    borderColor && !symbol.startsWith('triangle') ? `border: 1px ${borderShape ?? 'solid'} ${borderColor};` : undefined

  // Check for gradient
  if (color && typeof color === 'object' && 'stops' in color) {
    // Build a CSS linear-gradient from the stops
    const stops = color.stops
      .map((stop: Highcharts.GradientColorStopObject) => {
        const pos = stop[0]
        const colorStop = stop[1]
        return `${colorStop} ${pos * 100}%`
      })
      .join(', ')
    // Default to left-right horizontal gradient
    symbolStyle = `background: linear-gradient(0deg, ${stops});
        ${border ?? ''}`
  } else {
    symbolStyle = `background-color: ${typeof color === 'string' ? color : '#000'};
      ${border ?? ''}`
  }
  return symbolStyle
}

export type SymbolClass =
  | 'ChartCardSymbol-square'
  | 'ChartCardSymbol-circle'
  | 'ChartCardSymbol-diamond'
  | 'ChartCardSymbol-triangle'
  | 'ChartCardSymbol-triangle-down'
